import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Users, Crown, Sparkles, ArrowDownToLine, TrendingUp, RefreshCw, Copy, CheckCircle2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import PartnerLayout from '../../components/PartnerLayout';
import { partnerPortalAPI } from '../../lib/partnerApi';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

const StatCard = ({ title, value, icon: Icon, color, sub, testid }) => (
  <div data-testid={testid} className="bg-card rounded-xl border border-border p-5 shadow-sm">
    <div className="flex items-center justify-between">
      <p className="text-sm font-medium text-muted-foreground">{title}</p>
      <div className={cn('p-2.5 rounded-lg', color)}><Icon className="w-4 h-4 text-white" /></div>
    </div>
    <p className="text-3xl font-bold text-foreground mt-3">{value ?? '—'}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

const Tooltip2 = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg shadow p-3 text-sm">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map(p => <p key={p.dataKey} style={{ color: p.color }}>{p.name}: {p.value}</p>)}
    </div>
  );
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

const PartnerDashboardPage = () => {
  const { partner } = usePartnerAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await partnerPortalAPI.dashboard();
      setData(res.data);
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const referralCode = data?.stats?.referral_code;
  const referralUrl = referralCode
    ? `${window.location.origin}/register?ref=${referralCode}`
    : null;

  const copyCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyUrl = () => {
    if (referralUrl) {
      navigator.clipboard.writeText(referralUrl);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    }
  };

  if (loading && !data) return (
    <PartnerLayout>
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    </PartnerLayout>
  );

  const s = data?.stats || {};
  const trend = data?.seven_day_trend || [];
  const recent = data?.recent_referrals || [];

  return (
    <PartnerLayout>
      <div data-testid="partner-dashboard" className="space-y-6 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Welcome back, {partner?.partner_name}</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="partner-refresh-btn">
            <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} /> Refresh
          </Button>
        </div>

        {/* Referral code + URL banner */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-emerald-800">Your Referral Code</p>
              <p className="text-2xl font-bold text-emerald-700 mt-0.5 tracking-widest">{s.referral_code || '—'}</p>
            </div>
            <button
              onClick={copyCode}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold transition-colors"
              data-testid="copy-referral-code-btn"
            >
              {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Code'}
            </button>
          </div>

          {referralUrl && (
            <div className="border-t border-emerald-200 pt-3">
              <p className="text-xs font-medium text-emerald-700 mb-1.5">Registration Link</p>
              <div className="flex items-center gap-2 bg-card border border-emerald-200 rounded-lg px-3 py-2">
                <span className="text-xs text-emerald-800 font-mono flex-1 truncate" data-testid="referral-url">
                  {referralUrl}
                </span>
                <button
                  onClick={copyUrl}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold transition-colors shrink-0"
                  data-testid="copy-referral-url-btn"
                >
                  {copiedUrl ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copiedUrl ? 'Copied!' : 'Copy URL'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard testid="kpi-total-signups" title="Total Signups" value={s.total_signups} icon={Users} color="bg-indigo-500" sub="all time" />
          <StatCard testid="kpi-paid-customers" title="Paid Customers" value={s.total_paid_customers} icon={Crown} color="bg-purple-500" />
          <StatCard testid="kpi-total-earnings" title="Total Earnings" value={`$${(s.total_earnings || 0).toFixed(2)}`} icon={TrendingUp} color="bg-emerald-500" />
          <StatCard testid="kpi-pending-balance" title="Pending Balance" value={`$${(s.pending_balance || 0).toFixed(2)}`} icon={ArrowDownToLine} color="bg-orange-500" sub={`$${(s.total_withdrawn || 0).toFixed(2)} withdrawn`} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard testid="kpi-new-month" title="New Signups (Month)" value={s.new_this_month} icon={Users} color="bg-blue-500" />
          <StatCard testid="kpi-new-week" title="New Signups (7d)" value={s.recent_signups_7d} icon={Users} color="bg-sky-500" />
          <StatCard testid="kpi-premium-commission" title="Premium Commission" value={`${s.premium_commission_percent || 0}%`} icon={Crown} color="bg-violet-500" />
          <StatCard testid="kpi-ai-commission" title="AI Plan Commission" value={`${s.ai_commission_percent || 0}%`} icon={Sparkles} color="bg-pink-500" />
        </div>

        {/* 7-day chart */}
        <div data-testid="chart-7day" className="bg-card rounded-xl border border-border p-5 shadow-sm">
          <h2 className="text-base font-semibold mb-4">7-Day Signup Trend</h2>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<Tooltip2 />} />
              <Line type="monotone" dataKey="signups" name="Signups" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Recent referrals */}
        <div data-testid="table-recent-referrals" className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
          <div className="p-5 border-b border-border">
            <h2 className="text-base font-semibold">Recent Referrals</h2>
          </div>
          <div className="divide-y divide-border">
            {recent.length === 0 ? (
              <p className="px-5 py-8 text-sm text-muted-foreground text-center">No referrals yet</p>
            ) : (
              recent.map((r, i) => {
                const c = r.customer_id || {};
                return (
                  <div key={r._id || i} className="flex items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-sm shrink-0">
                        {(c.name || c.email || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{c.name || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{c.email}</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">{formatDate(r.signup_date)}</p>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </PartnerLayout>
  );
};

export default PartnerDashboardPage;
